/**
 * Map Google Directions statuses / server transport errors → parent-facing copy.
 * Prefer Google’s own wording when it isn’t technical (API keys, billing, etc.).
 */

import { UI_MSG } from "../../config/uiMessages";

function isTechnicalGoogleMessage(msg) {
  const s = String(msg || "").toLowerCase();
  if (!s.trim()) return true;
  return (
    s.includes("api key") ||
    s.includes("apikey") ||
    s.includes("billing") ||
    s.includes("referer") ||
    s.includes("referrer") ||
    s.includes("permission denied") ||
    s.includes("enable the") ||
    s.includes("not authorized") ||
    s.includes("invalid request") ||
    s.includes("this api project") ||
    s.includes("legacy api")
  );
}

/** Google Directions `status` → parent message (null = use generic / empty). */
export function messageForGoogleDirectionsStatus(status, errorMessage) {
  const st = String(status || "").toUpperCase();

  if (errorMessage && !isTechnicalGoogleMessage(errorMessage)) {
    return String(errorMessage).trim();
  }

  switch (st) {
    case "OK":
      return null;
    case "ZERO_RESULTS":
      return UI_MSG.TRANSPORT_NONE;
    case "NOT_FOUND":
      return "We couldn’t locate one of the places for this journey.";
    case "MAX_ROUTE_LENGTH_EXCEEDED":
      return "This journey is too long to show travel options.";
    case "OVER_QUERY_LIMIT":
    case "OVER_DAILY_LIMIT":
      return "Travel lookup is temporarily limited. Please try again shortly.";
    case "REQUEST_DENIED":
      return "Travel lookup isn’t available right now. Please try again later.";
    case "UNKNOWN_ERROR":
      return "Travel lookup failed temporarily. Please try again.";
    case "INVALID_REQUEST":
      return UI_MSG.TRANSPORT_LOAD_FAILED;
    default:
      return null;
  }
}

/** Prefer service/limit messages as hard errors; ZERO_RESULTS as empty notice. */
export function isTransportServiceFailureStatus(status) {
  const st = String(status || "").toUpperCase();
  return (
    st === "OVER_QUERY_LIMIT" ||
    st === "OVER_DAILY_LIMIT" ||
    st === "REQUEST_DENIED" ||
    st === "UNKNOWN_ERROR" ||
    st === "INVALID_REQUEST"
  );
}

/** HTTP / thrown transport errors from our API. */
export function messageForTransportHttpError(err) {
  const status = err?.status;
  const raw = err?.message || (err?.body && err.body.error) || "";

  if (raw && !isTechnicalGoogleMessage(raw)) {
    // Our server may already send parent copy
    if (
      !String(raw).toLowerCase().includes("google maps api key") &&
      !String(raw).toLowerCase().includes("not configured")
    ) {
      return String(raw);
    }
  }

  if (status === 400) {
    return "We need your home location and school to show travel options.";
  }
  if (status === 404) {
    return "We couldn’t find that school.";
  }
  if (status === 503 || status === 502) {
    return UI_MSG.SERVER_UNREACHABLE;
  }

  return UI_MSG.TRANSPORT_LOAD_FAILED;
}
